/**
 * ScoutDegradedBanner — calm advisory shown above SignalReview when the
 * Scout run fell back to deterministic classification (OpenAI quota
 * exhausted, fallback providers also out, or no LLM keys configured).
 *
 * Two-tier message:
 *   - Admins (integration:view) see the full provider context including
 *     which providers were attempted.
 *   - Operators / Viewers see a softer message without provider internals.
 *
 * Rendering rules:
 *   - Hidden if the run was not degraded.
 *   - Hidden if no warnings were attached to the response.
 *   - Single warning shown (de-duped server-side by `kind`).
 */
import React from 'react';
import { useAuth } from '../context/AuthContext';

export default function ScoutDegradedBanner({ warnings, degraded }) {
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission && (hasPermission('integration:view') || hasPermission('integration:manage'));

  if (!degraded || !Array.isArray(warnings) || warnings.length === 0) return null;
  const w = warnings[0];

  const tone = w.kind === 'provider_unavailable'
    ? { bg: '#fee2e2', border: '#fecaca', dot: '#b91c1c', text: '#991b1b' }
    : { bg: '#fef3c7', border: '#fde68a', dot: '#b45309', text: '#92400e' };

  const message = isAdmin ? w.admin_message : w.user_message;
  const attempts = isAdmin ? (w.details?.provider_attempts || []) : [];

  return (
    <div
      data-testid="scout-degraded-banner"
      role="status"
      aria-live="polite"
      style={{
        margin: '8px 0 12px', padding: '10px 14px', borderRadius: 6,
        background: tone.bg, border: `1px solid ${tone.border}`, fontSize: 13, color: tone.text,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span aria-hidden style={{
          width: 8, height: 8, borderRadius: '50%', background: tone.dot, flex: '0 0 auto',
        }} />
        <span style={{ flex: 1 }}>{message}</span>
      </div>
      {attempts.length > 0 && (
        <div data-testid="scout-degraded-attempts" style={{
          marginTop: 8, paddingTop: 8, borderTop: `1px solid ${tone.border}`,
          fontSize: 11, color: tone.text, opacity: 0.9,
        }}>
          <span style={{ fontWeight: 600 }}>Provider chain:</span>{' '}
          {attempts.map((a, i) => (
            <span key={i} style={{ marginRight: 10 }}>
              {a.provider} → {a.success ? 'ok' : (a.error_type || 'failed')}
              {a.fallback_used ? ' (fallback)' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
